import {
  email,
  SimpleForm,
  ImageInput,
  TextInput,
  FormDataConsumer,
  ImageField,
  ReferenceInput,
  AutocompleteInput,
  ArrayInput,
  SimpleFormIterator,
  useGetOne,
  useRecordContext,
} from "react-admin";
import { Box, FormControlLabel, Grid, Switch, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { statesList } from "../../../lib/constants";
import StateCheckboxArrayInput from "../StateCheckboxArrayInput";
import CarrierOptOutTable from "../CarrierOptOutTable";
import Principals from "./Principals";
import { PurlCopyButton, PurlOpenButton, PurlReadonly } from "./purl";
import { useWatch } from "react-hook-form";

const validateEmail = email();

const hasCustomDomain = (formData) => !!formData.domain?.length;

const TopOfHierarchyField = () => {
  const uplineId = useWatch({ name: "upline" });

  const { data: uplineAgency, isLoading: isLoadingUpline } = useGetOne(
    "agency",
    { id: uplineId },
    { enabled: !!uplineId }
  );

  const topId = uplineAgency?.topOfHierarchy || uplineAgency?.id;
  const needsSeparateFetch = topId && topId !== uplineAgency?.id;

  const { data: topAgency, isLoading: isLoadingTop } = useGetOne(
    "agency",
    { id: topId },
    { enabled: !!needsSeparateFetch }
  );

  let displayName = "N/A";
  if (uplineId) {
    if (isLoadingUpline || (needsSeparateFetch && isLoadingTop)) {
      displayName = "Loading...";
    } else if (uplineAgency) {
      displayName = (needsSeparateFetch ? topAgency?.name : uplineAgency.name) || "N/A";
    }
  }

  return (
    <div>
      <Typography variant="caption" color="textSecondary">
        Top of Hierarchy
      </Typography>
      <Typography variant="body1">{displayName}</Typography>
    </div>
  );
};

const UplineInput = ({ disabled }) => {
  const record = useRecordContext();
  return (
    <ReferenceInput reference="agency" source="upline" disabled={disabled}>
      <AutocompleteInput
        label="Upline Agency"
        matchSuggestion={(filter, choice) => {
          if (record && choice.id === record.id) return false;
          return choice.name?.toLowerCase().includes(filter.toLowerCase());
        }}
      />
    </ReferenceInput>
  );
};

// #5/#8 — per-carrier state limits for an agency's agents (a carrier with an
// entry is available only in the listed states for this agency). Applied GA sees
// this as "State overrides"; a principal sees it as "State availability".
const CarrierStateEditor = ({ statesLabel }) => (
  <ArrayInput source="carrierStateOverrides" label={false}>
    <SimpleFormIterator inline>
      <ReferenceInput source="carrier" reference="carrier">
        <AutocompleteInput optionText={(c) => c.value} label="Carrier" />
      </ReferenceInput>
      <StateCheckboxArrayInput
        source="states"
        label={statesLabel}
        choices={statesList}
      />
    </SimpleFormIterator>
  </ArrayInput>
);

const AgencyFields = ({ toolbar }) => {
  const { user, isFullAdmin, isStandardUser, isAgencyAdmin } = useAuth();
  const [carrierTab, setCarrierTab] = useState(0);
  return (
    <SimpleForm toolbar={toolbar}>
      <FormDataConsumer>
        {({ formData, ...rest }) => (
          <>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextInput
                  fullWidth
                  isRequired
                  source="name"
                  disabled={isStandardUser(user)}
                />
              </Grid>
              <Grid item xs={4}>
                <TextInput
                  fullWidth
                  source="contactFirstName"
                  disabled={isStandardUser(user)}
                />
              </Grid>
              <Grid item xs={4}>
                <TextInput
                  fullWidth
                  source="contactLastName"
                  disabled={isStandardUser(user)}
                />
              </Grid>
              <Grid item xs={4}>
                <TextInput
                  fullWidth
                  validate={validateEmail}
                  source="contactEmail"
                  disabled={isStandardUser(user)}
                />
              </Grid>

              <Grid item xs={4}>
                <TextInput
                  fullWidth
                  source="notifyContractRequestEmail"
                  label="Notification email for Contract Requests"
                  disabled={isStandardUser(user)}
                />
              </Grid>
              <Grid item xs={4}>
                <TextInput
                  fullWidth
                  source="notifyGARequestEmail"
                  label="Notification email for GA Requests"
                  disabled={isStandardUser(user)}
                />
              </Grid>
              {/* #86 - ACE pickers list Applied GA admins only (ACEs are staff),
                  not the whole users collection full of principals. */}
              <Grid item xs={6}>
                <ReferenceInput
                  reference="user"
                  source="defaultPIFSpecialist"
                  filter={{ role: "admin" }}
                  disabled={isStandardUser(user)}
                >
                  <AutocompleteInput label="Default Contract Request ACE" />
                </ReferenceInput>
              </Grid>
              <Grid item xs={6}>
                <ReferenceInput
                  reference="user"
                  source="defaultGASpecialist"
                  filter={{ role: "admin" }}
                  disabled={isStandardUser(user)}
                >
                  <AutocompleteInput label="Default GA ACE" />
                </ReferenceInput>
              </Grid>

              {(isFullAdmin(user) || isStandardUser(user)) && (
                <Grid item xs={4}>
                  <TextInput
                    fullWidth
                    source="aceRoutingKey"
                    label="ACE Routing Key"
                    helperText="Stable key the ACE router matches on (e.g. PGIA, QIA, FFSG, SIMPLER_HORIZONS). Leave blank to route by FSD/territory."
                    disabled={isStandardUser(user)}
                  />
                </Grid>
              )}

              <Grid item xs={4}>
                <UplineInput disabled={!isFullAdmin(user)} />
              </Grid>
              <Grid item xs={4}>
                <TopOfHierarchyField />
              </Grid>

              {/* #2 — read-only source identifiers preserved from the imported
                  agency sheet (Applied GA staff only). */}
              {(isFullAdmin(user) || isStandardUser(user)) && (
                <>
                  <Grid item xs={4}>
                    <TextInput
                      fullWidth
                      source="sourceId"
                      label="Source Agency ID (imported)"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextInput
                      fullWidth
                      source="parentSourceId"
                      label="Source Parent Agency ID (imported)"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextInput
                      fullWidth
                      source="uplineName"
                      label="Source Upline Agency (imported)"
                      disabled
                    />
                  </Grid>
                </>
              )}

              {/* #85 — who can log in and run this agency, with password
                  actions. Applied GA staff only; a principal editing their own
                  PURL info does not see it. */}
              {!isAgencyAdmin(user) && (
                <Grid item xs={12}>
                  <Principals agencyId={formData.id} />
                </Grid>
              )}

              {/* #5 — carrier availability for this agency. Every carrier is
                  available by default; these controls only capture the
                  exceptions. Applied GA gets three tabs (turn off for agents,
                  block entirely, limit by state); a principal gets the turn-off
                  grid and their own state-availability section (no blocking). */}
              {isFullAdmin(user) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Carrier availability for this agency
                  </Typography>
                  <Tabs
                    value={carrierTab}
                    onChange={(e, v) => setCarrierTab(v)}
                    sx={{ mb: 1 }}
                  >
                    <Tab label="Turned off for agents" />
                    <Tab label="Blocked" />
                    <Tab label="State overrides" />
                  </Tabs>
                  <Box sx={{ display: carrierTab === 0 ? "block" : "none" }}>
                    <CarrierOptOutTable
                      source="disabledCarriers"
                      addLabel="Add a carrier to turn off"
                      actionLabel="Turn on"
                      dateLabel="Turned off"
                      helperText="Turned-off carriers are hidden from this agency's agents. The principal can turn them back on."
                    />
                  </Box>
                  <Box sx={{ display: carrierTab === 1 ? "block" : "none" }}>
                    <CarrierOptOutTable
                      source="blockedCarriers"
                      addLabel="Add a carrier to block"
                      actionLabel="Unblock"
                      dateLabel="Blocked"
                      helperText="Blocked carriers are hidden from the agency entirely, the principal included."
                    />
                  </Box>
                  <Box sx={{ display: carrierTab === 2 ? "block" : "none" }}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Limit a carrier to specific states for this agency's agents.
                      Carriers without an entry use the carrier's default
                      availability.
                    </Typography>
                    <CarrierStateEditor statesLabel="Available states (this agency)" />
                  </Box>
                </Grid>
              )}

              {isAgencyAdmin(user) && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Carriers turned off for your agents
                    </Typography>
                    <CarrierOptOutTable
                      source="disabledCarriers"
                      addLabel="Add a carrier to turn off"
                      actionLabel="Turn on"
                      dateLabel="Turned off"
                      helperText="Turned-off carriers can't be picked by your agents. You can turn them back on anytime."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      State availability
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      Limit a carrier to specific states for your agents. Carriers
                      without an entry are available in all their default states.
                    </Typography>
                    <CarrierStateEditor statesLabel="Available states" />
                  </Grid>
                </>
              )}

              {/* #86 - on CREATE the domain is generated from the name and
                  presented in the link modal afterward, so no domain input is
                  shown here; it stays editable on the edit page. */}
              {formData.id && (
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch checked={hasCustomDomain(formData)} disabled />
                    }
                    label="Custom Domain Enabled"
                  />

                  {/* Full admins edit the domain; everyone else (principals,
                      lite admins, read-only staff) gets the PURL read-only with
                      a copy button so they can still grab the link to share. */}
                  {isFullAdmin(user) ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextInput fullWidth label="Domain" source="domain" />
                      <PurlOpenButton domain={formData.domain} icon />
                      <PurlCopyButton domain={formData.domain} icon />
                    </Box>
                  ) : (
                    <PurlReadonly domain={formData.domain} />
                  )}
                </Grid>
              )}

              <Grid item xs={6} style={{ textAlign: "center" }}>
                <ImageInput
                  source="logo"
                  accept={[".jpg", ".jpeg", ".png", ".webp"].join(",")}
                  disabled={isStandardUser(user)}
                >
                  <ImageField source="src" title="title" />
                </ImageInput>
              </Grid>
            </Grid>
          </>
        )}
      </FormDataConsumer>
    </SimpleForm>
  );
};

export default AgencyFields;
